import React, { useEffect, useState } from "react";
import { getPrompts, updatePrompt, setLock } from "../services/api";
import { EMU } from "../constants";
import VendorBadge from "../components/VendorBadge";

// Effective generation params for the model-config tables. Mirrors the server's per-provider behavior
// (see mari-repeatability-server providers/* + prompt_store config strings): OpenAI reasoning models drop
// temperature & seed; Claude-5.x with adaptive thinking requires temperature unset (+ Anthropic has no seed);
// Gemini 3.x isn't SDK-steerable so it runs at its vendor default. `kind` = history | emulator | evaluator.
const paramCells = (provider, model, kind, effRaw) => {
  const eff = effRaw || (kind === "evaluator" ? "high" : kind === "emulator" ? "none" : "medium");
  if (provider === "openai") return { reasoning: eff, temperature: "n/a", seed: "n/a" };
  if (provider === "anthropic") {
    if (/^claude-[a-z]+-5(?:-\d+)?$/.test(model)) return { reasoning: eff, temperature: "n/a", seed: "n/a" };
    return { reasoning: "off", temperature: kind === "emulator" ? "0" : "default", seed: "n/a" };
  }
  if (provider === "google")
    return { reasoning: `${kind === "emulator" ? "minimal" : eff} (default)`,
             temperature: kind === "emulator" ? "0" : "default", seed: kind === "emulator" ? "7" : "—" };
  return { reasoning: eff, temperature: "—", seed: "—" };
};

const LABELS = {
  emu_base: "Patient-Emulator — Base Instructions",
  history_baseline: "LLM History Tool — Baseline Prompt",
};
const personaLabel = (k) => "Persona · " + k.replace("persona:", "");

export default function PromptStudio() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [msg, setMsg] = useState("");

  const load = () => getPrompts().then((d) => { setData(d); setDrafts({}); });
  useEffect(() => { load(); }, []);

  if (!data) return <div className="page-head"><h2>Prompt Studio</h2></div>;
  const locked = data.locked;

  const save = async (kind) => {
    setMsg("");
    try {
      await updatePrompt(kind, drafts[kind]);
      await load();
      setMsg("Saved " + kind);
    } catch (e) {
      setMsg(e?.response?.status === 423 ? "Locked — edits are frozen." : "Save failed.");
    }
  };

  const toggleLock = async () => { await setLock(!locked); await load(); };

  const promptCard = (p) => (
    <div className="card" key={p.kind}>
      <div className="between">
        <h3>{LABELS[p.kind] || personaLabel(p.kind)}</h3>
        <span className="chip">{p.content_hash}</span>
      </div>
      <textarea rows={p.kind.startsWith("persona:") ? 8 : 10}
        value={drafts[p.kind] ?? p.content}
        disabled={locked}
        onChange={(e) => setDrafts({ ...drafts, [p.kind]: e.target.value })} />
      {!locked && drafts[p.kind] !== undefined && drafts[p.kind] !== p.content && (
        <div className="flex" style={{ marginTop: 8 }}>
          <button className="btn primary sm" onClick={() => save(p.kind)}>Save</button>
        </div>
      )}
    </div>
  );

  const order = ["emu_base", ...data.prompts.filter((p) => p.kind.startsWith("persona:")).map((p) => p.kind).sort(), "history_baseline"];
  const byKind = Object.fromEntries(data.prompts.map((p) => [p.kind, p]));

  const roles = data.model_config.roles || {};
  const historyRows = ["history:openai", "history:anthropic", "history:google"]
    .filter((k) => roles[k])
    .map((k) => ({ provider: roles[k].provider, model: roles[k].model, config: roles[k].config,
                   ...paramCells(roles[k].provider, roles[k].model, "history", roles[k].reasoning_effort) }));
  const emuRole = roles.emulator || {};
  const emuRows = Object.entries(EMU).map(([provider, model]) => ({
    provider, model, config: emuRole.config,
    ...paramCells(provider, model, "emulator", emuRole.reasoning_effort) }));
  const evalRows = roles.evaluator
    ? [{ provider: roles.evaluator.provider, model: roles.evaluator.model, config: roles.evaluator.config,
         ...paramCells(roles.evaluator.provider, roles.evaluator.model, "evaluator", roles.evaluator.reasoning_effort) }]
    : [];

  // Shared column widths so the three tables' columns line up vertically (each <table> otherwise
  // sizes its own columns). table-layout:fixed makes the widths authoritative.
  const cfgCols = ["20%", "28%", "18%", "17%", "17%"];
  const cfgTable = (title, rows) => (
    <div style={{ marginTop: 14 }}>
      <h4 style={{ margin: "0 0 6px" }}>{title}</h4>
      <table style={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>{cfgCols.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <thead><tr><th>Provider</th><th>Model</th><th>Reasoning</th><th>Temperature</th><th>Seed</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.provider + r.model} title={r.config || ""}>
              <td><VendorBadge vendor={r.provider} /></td>
              <td className="mono">{r.model}</td>
              <td className="mono">{r.reasoning}</td>
              <td className="mono">{r.temperature}</td>
              <td className="mono">{r.seed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="page-head"><h2>Prompt Studio</h2></div>
      <div className={`banner ${locked ? "ok" : "warn"}`}>
        <div>
          <b>{locked ? "Locked at pre-registration." : "Unlocked — iterating."}</b>{" "}
          {locked ? "Edits are frozen; runs pin these versions." : "Runs still stamp a content-hash of each prompt used."}
        </div>
        <div className="spacer" />
        <button className={`btn ${locked ? "" : "primary"} sm`} onClick={toggleLock}>
          {locked ? "Unlock" : "Lock (pre-registration)"}
        </button>
      </div>
      {msg && <div className="muted" style={{ marginBottom: 10 }}>{msg}</div>}

      {order.map((k) => byKind[k] && promptCard(byKind[k]))}

      <div className="card">
        <h3>Model config <span className="hash">v{data.model_config.version_no} · {data.model_config.content_hash}</span></h3>
        {cfgTable("History taking (SUT)", historyRows)}
        {cfgTable("Patient emulator", emuRows)}
        {cfgTable("Evaluator", evalRows)}
        <p className="muted" style={{ fontSize: 11, marginTop: 12 }}>
          <b>n/a</b> = the model ignores this parameter (OpenAI reasoning models drop temperature &amp; seed;
          Claude-5.x with thinking-on requires temperature unset; Anthropic has no seed). Gemini 3.x isn’t
          SDK-steerable for reasoning, so it runs at its vendor default (≈ the level shown). SUTs use
          vendor-default temperature by design; the emulator is pinned (temp&nbsp;0&nbsp;+&nbsp;seed&nbsp;7)
          where the model allows it. Hover a row for the full config string.
        </p>
      </div>
    </>
  );
}
